import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { AgGridModule } from 'ag-grid-angular';
import { MatProgressBarModule } from '@angular/material/progress-bar';

import { EntityList } from '../../objects/entity-list';
import { WorkflowRequestsService } from '../../service/entity-services/workflow-requests.service';
import { WorkflowRequest } from '../../objects/workflow';
import * as workflowColumns from '../../shared/entity-columns/workflow-columns';

/**
 * My Work → Workflow: the requests currently assigned to the signed-in user.
 *
 * This component previously rendered a permanently empty grid. Its `ngOnInit` and
 * `updateUserView` were entirely commented out, and — despite the name — it was not
 * built on workflow data at all: it injected `CriticalDatesService` and its columns
 * (`type`, `owner`, `assignedBy`, `eventDate`, `reminderDate`) were critical-date
 * columns. It was an abandoned different feature wearing the Workflow name.
 *
 * Rebuilt against `WorkflowRequestsService`, filtering on `assignedTo`.
 *
 * NOTE on what "assigned to me" means today: new requests are assigned to whoever
 * creates them, so until someone reassigns a request with the Assignee picker on the
 * workflow edit form, this list is effectively "requests I opened". That is the
 * intended starting state — the hand-off is the reassignment, not the creation.
 */
@Component({
  selector: 'app-workflow-tasking-list',
  templateUrl: './workflow-tasking-list.component.html',
  styleUrls: ['./workflow-tasking-list.component.css'],
  standalone: true,
  imports: [
    FormsModule,
    AgGridModule,
    MatProgressBarModule
  ]
})
export class WorkflowTaskingListComponent extends EntityList implements OnInit, OnDestroy {
  // `authService` is inherited from the Entity base class — don't re-inject it here,
  // a narrower visibility on the subclass breaks the base-class contract (TS2415).
  private workflowService = inject(WorkflowRequestsService);
  private navRouter = inject(Router);
  private destroy$ = new Subject<void>();

  waitingForFilterResults = true;
  workflowTaskingList: WorkflowRequest[] = [];
  gridApi = null;

  private currentUserEmail = '';

  taskingColumns = [
    workflowColumns.colIsNew,
    workflowColumns.colName,
    workflowColumns.colPhase,
    workflowColumns.colStatus,
    workflowColumns.colDocumentType,
    workflowColumns.colPriority,
    workflowColumns.colRequestor,
    workflowColumns.colDateRequested,
    workflowColumns.colDaysInProcess
  ];

  constructor() {
    super();
  }

  async ngOnInit(): Promise<void> {
    try {
      // SSO-safe resolver — decodes the Cognito ID token under the hosted-UI code
      // flow, which is what cbhi uses.
      this.currentUserEmail = (await this.authService.getUserEmail()) ?? '';
    } catch (e) {
      console.warn('[WorkflowTaskingList] failed to resolve current user', e);
    }

    if (!this.currentUserEmail) {
      // Without an identity there is no "mine" to filter on. Show nothing rather
      // than everything — this is a personal work queue, not the full list.
      this.workflowTaskingList = [];
      this.waitingForFilterResults = false;
      return;
    }

    // Subscribing is what triggers the cache load (the service refreshes lazily on
    // first read), so no explicit refresh call is needed here.
    this.workflowService.allRequests$
      .pipe(takeUntil(this.destroy$))
      .subscribe(requests => {
        this.workflowTaskingList = (requests || []).filter(r => this.isMine(r));
        this.waitingForFilterResults = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * `assignedTo` is normally a plain email string, but hydrated records can carry a
   * user object — the grid's own `userValueGetter` handles both, so this does too.
   */
  private isMine(request: WorkflowRequest): boolean {
    const assigned: any = request?.assignedTo;
    if (!assigned) return false;
    const email = typeof assigned === 'string' ? assigned : (assigned.email ?? '');
    return email.trim().toLowerCase() === this.currentUserEmail.trim().toLowerCase();
  }

  onRowClicked(event: any): void {
    const workflowNum = event?.data?.workflowNum;
    if (workflowNum) {
      this.navRouter.navigate(['/securehome/workflow', workflowNum]);
    }
  }
}
